/**
 * useCurrentIntervalName Hook
 *
 * Hook customizado para monitorar alterações na configuração da fila digital
 * e retornar o nome do intervalo atual baseado nas regras de negócio:
 * - Se autoIntervalChange for true → usa currentInterval.name
 * - Se autoIntervalChange for false → usa selectedIntervalName
 */

import { useState, useEffect, useCallback } from 'react';
import type { FilaConfig } from '../types/config';
import { FILA_CONFIG_STORAGE_KEY } from '../types/config';

export function useCurrentIntervalName(): string {
  const [intervalName, setIntervalName] = useState<string>('Intervalo não configurado');

  // Função para obter o nome do intervalo baseado na configuração
  const getIntervalNameFromConfig = useCallback((config: FilaConfig | null): string => {
    if (!config) {
      return 'Intervalo não configurado';
    }

    const { autoIntervalChange, currentInterval, selectedIntervalName } = config;

    try {
      if (autoIntervalChange) {
        // Modo automático: prioridade máxima para currentInterval.name (salvo do /current)
        if (currentInterval?.name) {
          console.log('📝 Auto interval: usando currentInterval.name do /current:', currentInterval.name);
          return currentInterval.name;
        }
        // Fallback para selectedIntervalName (compatibilidade)
        if (selectedIntervalName) {
          console.log('📝 Auto interval: usando selectedIntervalName:', selectedIntervalName);
          return selectedIntervalName;
        }
        // Último fallback
        console.log('📝 Auto interval: sem nome disponível, usando padrão');
        return 'Intervalo automático';
      } else {
        // Modo manual: prioridade para selectedIntervalName
        if (selectedIntervalName) {
          console.log('📝 Manual interval: usando selectedIntervalName:', selectedIntervalName);
          return selectedIntervalName;
        }
        // Fallback para currentInterval.name
        if (currentInterval?.name) {
          console.log('📝 Manual interval: usando currentInterval.name:', currentInterval.name);
          return currentInterval.name;
        }
        // Último fallback
        console.log('📝 Manual interval: sem nome disponível, usando padrão');
        return 'Intervalo manual';
      }
    } catch (error) {
      console.error('Erro ao processar configuração do intervalo:', error);
      return 'Erro na configuração';
    }
  }, []);

  // Função para carregar configuração do localStorage e atualizar o nome
  const loadAndUpdateIntervalName = useCallback(() => {
    try {
      const storedConfig = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
      if (storedConfig) {
        const config: FilaConfig = JSON.parse(storedConfig);
        const newName = getIntervalNameFromConfig(config);
        setIntervalName(newName);
      } else {
        setIntervalName('Intervalo não configurado');
      }
    } catch (error) {
      console.error('Erro ao carregar configuração do localStorage:', error);
      setIntervalName('Erro ao carregar configuração');
    }
  }, [getIntervalNameFromConfig]);

  // Configurar listener para mudanças no storage (cross-tab synchronization)
  useEffect(() => {
    // Carregar configuração inicial
    loadAndUpdateIntervalName();

    // Listener para mudanças no localStorage (outras abas/janelas)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === FILA_CONFIG_STORAGE_KEY && event.newValue) {
        loadAndUpdateIntervalName();
      }
    };

    // Adicionar event listener
    window.addEventListener('storage', handleStorageChange);

    // Custom event para mudanças na mesma aba (triggerado pelo useFilaConfig)
    const handleCustomConfigChange = () => {
      loadAndUpdateIntervalName();
    };

    window.addEventListener('filaConfigChanged', handleCustomConfigChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('filaConfigChanged', handleCustomConfigChange);
    };
  }, [loadAndUpdateIntervalName]);

  return intervalName;
}